import { pubSub } from '@utils/PubSub';
import type { Nanobar } from 'types/dyInterfaces';
import Splide from '@splidejs/splide';
import { nanoSlide } from '@utils/NavResources';
export default class Nanobar {
  constructor(context) {
    this.awaitDY();
    this.element = context;
  }

  //wait for DY to fire custom event, or if timeout fires, then use fallback data
  awaitDY() {
    pubSub.subscribe('dy:nano:init', () => {
      const settings = window.Brooklinen?.nano[0] || {};
      this.mountNano(settings);
      pubSub.unsubscribe('dy:nano:init');
    });

    setTimeout(() => {
      if (!window.Brooklinen?.nanoLoaded) {
        let fallbackData;
        if (window.Brooklinen?.nano) {
          fallbackData = window.Brooklinen?.nano[0];
        } else {
          let parsedContent = JSON.parse(
            document.querySelector('#nanobar_fallback_data').textContent
          );
          fallbackData = parsedContent[0];
        }
        this.mountNano(fallbackData);
      }
    }, window.Brooklinen.dyExpiration || 5000);
  }

  //checks for all settings in the data object to both populate the text content and mount the slideshow
  async mountNano(data: Object) {
    const { element } = this;

    //check if user has opted in for the prospect discount
    const optedIn =
      document.cookie.indexOf('_dy_email_submited') != -1 &&
      document.cookie.indexOf('_dy_nano_updated') != -1;
    const nanoText = optedIn
      ? document.cookie.match(new RegExp('(^| )_dy_nano_updated=([^;]+)'))[2]
      : '';

    //populate slide content for the nanobar
    await this.populateTextContent(
      data?.slides,
      data?.modal_data,
      data?.settings?.opt_in_slide,
      nanoText
    );

    //initialize splide
    this.initNanoSlides(data?.slides, data?.settings?.slide_duration);

    //check if there is a timer countdown date, and show appropriate layout
    if (data?.timer?.countdown_date) {
      element.setAttribute('data-timer', true);
      this.initCountdown(data?.timer?.countdown_date, data?.timer?.timer_label);
    } else {
      element.setAttribute('data-timer', false);
      element.querySelector('.clock-wrapper')?.remove();
    }

    window.Brooklinen.nanoLoaded = true;

    //listen to the nudge overlay submitted event
    this.initNudgeListener(data);
  }

  //dynamically update the nanobar upon successful nudge overlay email submission
  initNudgeListener(data: Object) {
    const { element } = this;
    pubSub.subscribe('nudge:submitted', () => {
      document.cookie.match(new RegExp('(^| )_dy_nano_updated=([^;]+)'))[2];
      const optInText = document.cookie.match(
        new RegExp('(^| )_dy_nano_updated=([^;]+)')
      )[2];
      element
        .querySelectorAll(
          `.offer [data-slide="${data?.settings?.opt_in_slide}"] span.terms`
        )
        .forEach((el) => {
          el.remove();
        });
      element
        .querySelectorAll(
          `.offer [data-slide="${data?.settings?.opt_in_slide}"] a.terms`
        )
        .forEach((el) => {
          el.remove();
        });
      element
        .querySelectorAll(
          `.offer [data-slide="${data?.settings?.opt_in_slide}"] .text`
        )
        .forEach((el) => {
          el.innerHTML = optInText;
        });

      element.querySelector('.nano--type').style.maxWidth = `${
        this.setMaxTextWidth([
          ...data?.slides,
          { main_text: optInText, terms_text: '', terms_link_text: '' },
        ]) + 100
      }px`;

      this.initNanoSlides(data?.slides, data?.settings?.slide_duration, true);
    });
  }

  //initialize splide
  initNanoSlides(slides: Array, duration: String, refresh: Boolean) {
    const { element } = this;
    const slideLength = slides.filter((slide) => {
      return slide.main_text != '';
    }).length;
    element.setAttribute('data-slide-count', slideLength);
    const initOptions = {
      type: 'loop',
      autoplay: slideLength > 1 ? true : false,
      pauseOnHover: true,
      interval: duration || 7000,
      pagination: false,
      drag: slideLength > 1 ? true : false,
    };
    const splide = new Splide(
      element.querySelector('.nano--slide-container'),
      initOptions
    );

    setTimeout(() => {
      refresh ? splide.refresh() : splide.mount();
    }, 0);
  }

  //populate modal content, if applicable. leverages utils/overlay.js, ported over from Marlow + B4B
  initModal(modalData: Object) {
    const { element } = this;
    const modalRef: string = modalData?.content,
      modalTitle: string = modalData?.modal_title,
      modalCopy: string = modalData?.modal_copy,
      modalContainer: HTMLElement = element.querySelector(
        `content-drawer[data-label="${modalRef}"]`
      ),
      modalTitleContainer: HTMLElement = modalContainer?.querySelector(
        '.overlay__modal--title'
      ),
      modalCopyContainer: HTMLElement = modalContainer?.querySelector(
        '.overlay__modal--text'
      );
    modalTitleContainer.innerHTML = modalTitle;
    modalCopyContainer.innerHTML = modalCopy;
  }

  populateTextContent(
    slides: Array,
    modalData: Object,
    optinSlide: String,
    nanoText: String
  ) {
    const { element } = this;

    return new Promise((resolve) => {
      this.initModal(modalData);

      slides?.forEach((slide, index) => {
        if (slide.main_text != '') {
          element.querySelector('.splide__list').innerHTML += nanoSlide(
            slide,
            modalData,
            optinSlide,
            nanoText,
            index + 1
          );
        }

        if (index == slides.length - 1) {
          //reinitialize the event listeners in overlay.js by firing this event
          //demo: https://brooklinen-business-dev.myshopify.com/pages/demo-overlay
          document.dispatchEvent(new Event('theme:overlay:refresh'));
          element.querySelector('.nano--type').style.maxWidth = `${
            this.setMaxTextWidth([
              ...slides,
              { main_text: nanoText, terms_text: '', terms_link_text: '' },
            ]) + 100
          }px`;
          resolve();
        }
      });
    });
  }

  //estimate the expected width of each slide before it is rendered via splide
  //set the max width of the splide container to the largest width of the slide plus padding pixels

  setMaxTextWidth(slideArray: Array) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '14px BrandonTextWeb-Bold';
    let maxTextWidth = 0;
    slideArray.forEach((slide) => {
      const canvasText =
        slide.main_text + slide.terms_text + slide.terms_link_text;
      const textMetrics = context.measureText(
        canvasText.replace(/<[^>]*>/g, '')
      );
      const width = textMetrics.width;
      maxTextWidth = width > maxTextWidth ? width : maxTextWidth;
    });
    return maxTextWidth;
  }
  //initialize the countdown and use setInterval to run the countdown function every second
  initCountdown(date: Date, label: String): void {
    const { element } = this;
    element.querySelector('.clock--module-label').innerHTML = label;
    this.updateCountdown(date);
    this.countdown = setInterval(() => this.updateCountdown(date), 1000);
  }

  //update the countdown timer every second. if the countdown expires, then it is removed.
  updateCountdown(date: Date): void {
    const { element } = this;
    const clockWrapper = element.querySelector('.clock-wrapper');
    const countdownContainer = element.querySelector(
      '.clock--module-countdown'
    );
    const deadline = new Date(new Date(`${date.replace(/-/g, '/')}`)).getTime(),
      now = new Date().getTime(),
      distance = deadline - now;
    if (distance > 0) {
      clockWrapper.classList.remove('empty');
      let days = Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        ),
        minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const hoursCopy = hours == 1 ? ' hour, ' : ' hours, ',
        daysCopy = days == 1 ? ' day, ' : ' days, ';
      let countdownHTML = `&nbsp;${days > 0 ? days + daysCopy : ''}${
        hours > 0 ? hours + hoursCopy : ''
      }${minutes > 0 ? minutes + ' min,' : ''} ${seconds} sec`;
      countdownContainer.innerHTML = countdownHTML;
    } else {
      clearInterval(this.countdown);
      element.setAttribute('data-timer', false);
      clockWrapper.classList.add('empty');
      element.querySelector('.clock--module')?.remove();
    }
  }
}
